import React, { useEffect, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import QRCode from "qrcode";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { catColor, frDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapPin, Route, Warehouse, Clock, Navigation, Loader2, Printer } from "lucide-react";

const fmtDist = (m) => (m == null ? "—" : m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);
const fmtDur = (s) => {
  if (s == null) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
};
const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export default function Deliveries() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [depot, setDepot] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [ordered, setOrdered] = useState(null);
  const [summary, setSummary] = useState(null);
  const [useDepot, setUseDepot] = useState(true);
  const { org } = useAuth();
  const warehouses = org?.warehouses || [];
  const [depotChoice, setDepotChoice] = useState("default");
  const [whDepot, setWhDepot] = useState(null);

  const geocodeWh = async (wh) => {
    if (!wh?.address || !mapboxgl.accessToken) { setWhDepot({ name: wh?.name, address: wh?.address, coordinates: null }); toast.error("Adresse de l'entrepôt manquante"); return; }
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(wh.address)}.json?access_token=${mapboxgl.accessToken}&limit=1`;
      const r = await fetch(url); const j = await r.json();
      const c = j?.features?.[0]?.center || null;
      setWhDepot({ name: wh.name, address: wh.address, coordinates: c });
      if (!c) toast.error("Adresse de l'entrepôt introuvable"); else toast.success(`Départ défini : ${wh.name}`);
    } catch { setWhDepot({ name: wh.name, address: wh.address, coordinates: null }); toast.error("Géocodage impossible"); }
  };

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let map;
    (async () => {
      try {
        const { data } = await api.get("/config/mapbox");
        if (!data.token) { toast.error("Mapbox non configuré"); return; }
        mapboxgl.accessToken = data.token;
        map = new mapboxgl.Map({
          container: containerRef.current,
          style: "mapbox://styles/mapbox/dark-v11",
          center: [2.3522, 46.6],
          zoom: 4.5,
        });
        map.addControl(new mapboxgl.NavigationControl(), "top-right");
        map.on("load", () => setReady(true));
        mapRef.current = map;
      } catch (e) {
        toast.error("Impossible de charger la carte");
      }
    })();
    return () => { if (map) map.remove(); };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setOrdered(null);
    setSummary(null);
    try {
      const { data } = await api.get(`/logistics/deliveries?date=${date}`);
      setDepot(data.depot || null);
      setEvents(data.events || []);
    } catch (e) {
      toast.error("Erreur de chargement des livraisons");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const clearRoute = () => {
    const map = mapRef.current;
    if (map && map.getLayer("route")) map.removeLayer("route");
    if (map && map.getSource("route")) map.removeSource("route");
  };

  const list = ordered || events;
  const dep = depotChoice !== "default" && whDepot ? whDepot : depot;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    clearRoute();
    const bounds = new mapboxgl.LngLatBounds();
    let any = false;

    if (useDepot && dep?.coordinates) {
      const el = document.createElement("div");
      el.className = "flex h-7 w-7 items-center justify-center rounded-full bg-white text-black shadow-lg ring-2 ring-[#FF6A00]";
      el.innerHTML = "<span style='font-size:14px'>🏭</span>";
      const mk = new mapboxgl.Marker(el).setLngLat(dep.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 18 }).setHTML("<b>Dépôt</b><br/>" + String(dep.address || "").replace(/</g, "&lt;").replace(/>/g, "&gt;"))).addTo(map);
      markersRef.current.push(mk);
      bounds.extend(dep.coordinates);
      any = true;
    }

    list.forEach((e, i) => {
      const cc = catColor(e.category);
      const el = document.createElement("div");
      el.className = "flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white shadow-lg";
      el.style.background = cc.hex;
      el.textContent = ordered ? String(i + 1) : "•";
      const mk = new mapboxgl.Marker(el).setLngLat(e.coordinates)
        .setPopup(new mapboxgl.Popup({ offset: 18 }).setHTML("<b>" + String(e.title || "").replace(/</g, "&lt;").replace(/>/g, "&gt;") + "</b><br/>" + String(e.location || "").replace(/</g, "&lt;").replace(/>/g, "&gt;"))).addTo(map);
      markersRef.current.push(mk);
      bounds.extend(e.coordinates);
      any = true;
    });

    if (any) {
      try { map.fitBounds(bounds, { padding: 60, maxZoom: 13, duration: 600 }); } catch (err) { console.debug("fitBounds skipped", err?.message); }
    }
  }, [list, dep, useDepot, ready, ordered]);

  const optimize = async () => {
    if (events.length < 2) { toast.info("Au moins 2 événements requis pour optimiser"); return; }
    setOptimizing(true);
    try {
      const pts = [];
      if (useDepot && dep?.coordinates) pts.push(dep.coordinates);
      events.forEach((e) => pts.push(e.coordinates));
      const roundtrip = !!(useDepot && dep?.coordinates);
      const { data } = await api.post("/logistics/optimize", { coordinates: pts, roundtrip });
      const depotOffset = roundtrip ? 1 : 0;
      const seq = data.order
        .map((inputIdx) => inputIdx - depotOffset)
        .filter((evIdx) => evIdx >= 0 && evIdx < events.length);
      const seen = new Set();
      const reordered = [];
      seq.forEach((evIdx) => { if (!seen.has(evIdx)) { seen.add(evIdx); reordered.push(events[evIdx]); } });
      events.forEach((e, idx) => { if (!seen.has(idx)) reordered.push(e); });
      setOrdered(reordered);
      setSummary({ distance: data.distance, duration: data.duration });
      const map = mapRef.current;
      if (map && data.geometry) {
        clearRoute();
        map.addSource("route", { type: "geojson", data: { type: "Feature", geometry: data.geometry } });
        map.addLayer({ id: "route", type: "line", source: "route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": "#FF6A00", "line-width": 4, "line-opacity": 0.85 } });
      }
      toast.success("Tournée optimisée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Optimisation impossible");
    } finally {
      setOptimizing(false);
    }
  };

  const printSheet = async () => {
    if (!list.length) { toast.info("Aucune livraison à imprimer"); return; }
    const dateLabel = frDate(date);
    const depotBlock = (useDepot && dep?.coordinates)
      ? `<p class="depot"><b>Départ / retour :</b> ${esc(dep.name || "Dépôt")} — ${esc(dep.address || "")}</p>` : "";
    const sumBlock = summary
      ? `<p class="sum"><b>Distance :</b> ${fmtDist(summary.distance)} &nbsp;·&nbsp; <b>Durée estimée :</b> ${fmtDur(summary.duration)}</p>`
      : `<p class="sum warn">Tournée non optimisée — ordre indicatif</p>`;
    const rows = (await Promise.all(list.map(async (e, i) => {
      const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${e.coordinates[1]},${e.coordinates[0]}`;
      let qr = "";
      try { qr = await QRCode.toDataURL(navUrl, { margin: 1, width: 160 }); } catch (err) { console.debug("QR generation failed", err?.message); }
      return `
      <tr>
        <td class="num">${ordered ? i + 1 : "—"}</td>
        <td><div class="title">${esc(e.title)}</div><div class="cat">${esc(e.category || "")}</div></td>
        <td>${esc(e.location || "—")}</td>
        <td>${esc(e.contact_name || "—")}${e.contact_phone ? `<div class="ph">${esc(e.contact_phone)}</div>` : ""}</td>
        <td class="center">${e.guests ? esc(e.guests) + " pers." : "—"}</td>
        <td class="qr">${qr ? `<img src="${qr}" alt="QR"/>` : ""}</td>
        <td class="check"></td>
      </tr>`;
    }))).join("");
    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"/>
      <title>Feuille de tournée — ${esc(dateLabel)}</title>
      <style>
        @page { margin: 14mm; @bottom-right { content: "Page " counter(page) " / " counter(pages); font-size: 9px; color: #777; } }
        * { box-sizing: border-box; }
        body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #111; margin: 32px; }
        h1 { margin: 0 0 2px; font-size: 22px; }
        .brand { color: #FF6A00; font-weight: 700; letter-spacing: .5px; text-transform: uppercase; font-size: 12px; }
        .meta { color: #555; margin: 4px 0 14px; }
        .depot, .sum { margin: 4px 0; font-size: 13px; }
        .sum.warn { color: #a15c00; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 13px; }
        th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; vertical-align: top; }
        th { background: #FFF3E6; color: #7a3d00; font-size: 11px; text-transform: uppercase; letter-spacing: .4px; }
        td.num { width: 34px; text-align: center; font-weight: 700; color: #FF6A00; font-size: 15px; }
        td.center { text-align: center; }
        td.check { width: 46px; }
        td.qr { width: 96px; text-align: center; }
        td.qr img { width: 82px; height: 82px; display: block; margin: 0 auto; }
        .title { font-weight: 600; }
        .cat { color: #888; font-size: 11px; }
        .ph { color: #444; font-size: 12px; }
        .foot { margin-top: 22px; color: #999; font-size: 11px; text-align: center; }
        @media print { body { margin: 12mm; } }
      </style></head><body>
      <div class="brand">HalgarOS · Feuille de tournée</div>
      <h1>Livraisons du ${esc(dateLabel)}</h1>
      <div class="meta">${list.length} arrêt(s)</div>
      ${depotBlock}${sumBlock}
      <table>
        <thead><tr><th>#</th><th>Événement</th><th>Adresse</th><th>Contact</th><th>Invités</th><th>QR nav</th><th>Fait</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="foot">Généré par HalgarOS</div>
      <script>window.onload = function(){ window.print(); }</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Autorisez les pop-ups pour imprimer"); return; }
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="deliveries-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Livraisons</h1>
          <p className="mt-1 text-sm text-zinc-500">Carte des événements et optimisation de la tournée du jour.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="deliveries-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-44 border-zinc-800 bg-zinc-900" />
          {warehouses.length > 0 && (
            <select data-testid="deliveries-depot-select" value={depotChoice}
              onChange={(e) => { const v = e.target.value; setDepotChoice(v); if (v === "default") setWhDepot(null); else { const w = warehouses.find((x) => x.id === v); if (w) geocodeWh(w); } }}
              className="rounded-full border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-300 focus:border-[#FF6A00]/40 focus:outline-none">
              <option value="default">Départ : Adresse entreprise</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>Départ : {w.name}</option>)}
            </select>
          )}
          <button data-testid="deliveries-depot-toggle" onClick={() => setUseDepot((v) => !v)}
            className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition-colors ${useDepot ? "border-[#FF6A00]/50 bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 text-zinc-400 hover:bg-zinc-800"}`}>
            <Warehouse className="h-4 w-4" /> Dépôt {useDepot ? "inclus" : "exclu"}
          </button>
          <Button data-testid="deliveries-optimize" onClick={optimize} disabled={optimizing || events.length < 2}
            className="rounded-full bg-[#FF6A00] text-white hover:bg-[#e65f00]">
            {optimizing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Route className="mr-2 h-4 w-4" />}
            Optimiser la tournée
          </Button>
          <Button data-testid="deliveries-print" onClick={printSheet} disabled={list.length === 0} variant="outline"
            className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800">
            <Printer className="mr-2 h-4 w-4" /> Imprimer
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="order-2 space-y-2 lg:order-1">
          {summary && (
            <div className="flex items-center justify-between rounded-xl border border-[#FF6A00]/30 bg-[#FF6A00]/[0.06] p-3 text-sm" data-testid="deliveries-summary">
              <span className="flex items-center gap-2 text-[#FF6A00]"><Navigation className="h-4 w-4" /> {fmtDist(summary.distance)}</span>
              <span className="flex items-center gap-2 text-[#FF6A00]"><Clock className="h-4 w-4" /> {fmtDur(summary.duration)}</span>
            </div>
          )}
          {useDepot && dep && (
            <div className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-black ring-2 ring-[#FF6A00]"><Warehouse className="h-4 w-4" /></div>
              <div className="min-w-0"><p className="truncate text-sm font-medium text-white">Dépôt · {dep.name || "Départ"}</p><p className="truncate text-xs text-zinc-500">{dep.address || "—"}</p>{depotChoice !== "default" && !dep.coordinates && <p className="text-xs text-red-400">Adresse non localisée — dépôt ignoré dans l'optimisation.</p>}</div>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center rounded-xl border border-zinc-800 p-10 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : list.length === 0 ? (
            <p className="rounded-xl border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500" data-testid="deliveries-empty">
              Aucun événement géolocalisé pour cette date.
            </p>
          ) : (
            list.map((e, i) => {
              const cc = catColor(e.category);
              return (
                <div key={e.id} data-testid={`delivery-item-${e.id}`} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: cc.hex }}>
                    {ordered ? i + 1 : <MapPin className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{e.title}</p>
                    <p className="truncate text-xs text-zinc-500">{[e.location, e.contact_name].filter(Boolean).join(" · ") || "—"}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <a data-testid={`nav-gmaps-${e.id}`} href={`https://www.google.com/maps/dir/?api=1&destination=${e.coordinates[1]},${e.coordinates[0]}`}
                      target="_blank" rel="noopener noreferrer" title="Ouvrir dans Google Maps"
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-800 text-zinc-300 transition-colors hover:border-[#FF6A00]/50 hover:text-[#FF6A00]">
                      <MapPin className="h-4 w-4" />
                    </a>
                    <a data-testid={`nav-waze-${e.id}`} href={`https://waze.com/ul?ll=${e.coordinates[1]},${e.coordinates[0]}&navigate=yes`}
                      target="_blank" rel="noopener noreferrer" title="Ouvrir dans Waze"
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-800 text-zinc-300 transition-colors hover:border-[#33ccff]/60 hover:text-[#33ccff]">
                      <Navigation className="h-4 w-4" />
                    </a>
                    <span className={`ml-1 hidden text-xs sm:inline ${cc.text}`}>{cc.label}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="order-1 lg:order-2 lg:col-span-2">
          <div ref={containerRef} data-testid="deliveries-map" className="h-[420px] w-full overflow-hidden rounded-2xl border border-zinc-800 lg:h-[600px]" />
          <p className="mt-2 text-xs text-zinc-500">Astuce : sélectionnez une date, puis « Optimiser la tournée » pour ordonner les livraisons (départ/retour au dépôt).</p>
        </div>
      </div>
    </div>
  );
}
